import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import {
  fetchCareerRecords,
  fetchHeadToHead,
  fetchHeadToHeadMatrix,
  fetchLeaguePlayers,
  fetchPlayerHistory,
} from '../services/league'
import {
  Card,
  ConferenceBadge,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  Segmented,
  SortableHeaderRow,
  formatPct,
  formatRecord,
  formatSharePct,
  formatSigned,
  playerProfilePath,
  recordSortKey,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import type {
  CareerRecordRow,
  GameTypeFilter,
  HeadToHeadRow,
  SeasonRecordBreakdown,
} from '../types'
import './NerdData.css'

/**
 * @remarks
 * The `#` cell is a row counter rather than a standing, so it renumbers against
 * whatever order is on screen. Nothing here claims a seed, unlike the
 * conference tables, so there is no ordering it can contradict.
 */
const CAREER_COLUMNS: Array<SortableColumn<CareerRecordRow>> = [
  { key: 'rank', label: '#', className: 'rank' },
  { key: 'player', label: 'Player', sort: row => row.displayName },
  {
    key: 'gamesPlayed',
    label: 'GP',
    className: 'num',
    sortLabel: 'games played',
    sort: row => row.gamesPlayed,
  },
  {
    key: 'record',
    label: 'Record',
    className: 'record',
    hint: 'Sort by wins, then by fewest losses',
    sort: row => recordSortKey(row.wins, row.losses),
  },
  {
    key: 'pct',
    label: 'Pct',
    className: 'num',
    sortLabel: 'win percentage',
    sort: row => (row.gamesPlayed === 0 ? null : row.winPercentage),
  },
  {
    key: 'pointsFor',
    label: 'PF',
    className: 'num',
    sortLabel: 'points for',
    sort: row => row.pointsFor,
  },
  {
    key: 'pointsAgainst',
    label: 'PA',
    className: 'num',
    sortLabel: 'points against',
    sort: row => row.pointsAgainst,
  },
  {
    key: 'diff',
    label: 'Diff',
    className: 'num',
    sortLabel: 'point differential',
    sort: row => row.pointDifferential,
  },
  {
    key: 'p1Pct',
    label: 'P1%',
    className: 'num',
    sortLabel: 'P1 share',
    hint: 'Share of games this player had the P1 controller',
    sort: row => (row.gamesPlayed === 0 ? null : row.p1Percentage),
  },
  {
    key: 'championships',
    label: 'Titles',
    className: 'num',
    sortLabel: 'championships',
    hint: 'Championships won',
    sort: row => row.championships,
  },
  {
    key: 'runnerUps',
    label: '2nd',
    className: 'num',
    sortLabel: 'runner-up finishes',
    hint: 'Runner-up finishes',
    sort: row => row.runnerUps,
  },
]

/** Labels for the game-type filter, in the order they read as a narrowing. */
const GAME_TYPE_OPTIONS: Array<{ value: GameTypeFilter; label: string }> = [
  { value: 'all', label: 'All games' },
  { value: 'regular', label: 'Regular season' },
  { value: 'playoffs', label: 'Playoffs' },
]

/**
 * @param gameType - Which games the totals count. Refetched rather than
 * filtered here: the rows are already aggregates, so there is nothing in a
 * career row a client could subtract a postseason out of.
 *
 * @remarks
 * The chosen sort survives a change of scope. {@link useTableSort} holds its
 * state beside the rows rather than inside them, so a table sorted by points
 * for stays sorted by points for when the postseason is taken out from under
 * it.
 */
function CareerTable({ gameType }: { gameType: GameTypeFilter }) {
  const career = useAsync(() => fetchCareerRecords(gameType), [gameType])
  const sort = useTableSort(career.data ?? [], CAREER_COLUMNS)

  if (career.loading) return <Loading rows={8} />
  if (career.error) return <Notice tone="error">{career.error}</Notice>
  if (!career.data || career.data.length === 0) {
    return gameType === 'playoffs' ? (
      <EmptyState title="No playoff games on record yet">
        Games count here once they are marked as playoff — an admin can flag a result, a season
        in postseason stores every new game as one, and entering a game through a bracket does
        it automatically.
      </EmptyState>
    ) : (
      <EmptyState title="No career records yet">
        Records accumulate as games are recorded, across every season.
      </EmptyState>
    )
  }

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={CAREER_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map((row, index) => (
            <tr key={row.userId ?? row.displayName}>
              <td className="rank">{index + 1}</td>
              <td>
                <PlayerCell
                  name={row.displayName}
                  discordId={row.discordId}
                  avatar={row.discordAvatar}
                  to={row.userId === null ? undefined : playerProfilePath(row.userId)}
                />
              </td>
              <td className="num">{row.gamesPlayed}</td>
              <td className="record">{formatRecord(row.wins, row.losses, row.ties)}</td>
              <td className="num">{formatPct(row.winPercentage)}</td>
              <td className="num">{row.pointsFor}</td>
              <td className="num">{row.pointsAgainst}</td>
              <td className="num">{formatSigned(row.pointDifferential)}</td>
              <td className="num" title={`${row.p1Games} of ${row.gamesPlayed} as P1`}>
                {formatSharePct(row.gamesPlayed === 0 ? null : row.p1Percentage)}
              </td>
              <td className="num">{row.championships === 0 ? '—' : row.championships}</td>
              <td className="num">{row.runnerUps === 0 ? '—' : row.runnerUps}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const OPPONENT_COLUMNS: Array<SortableColumn<HeadToHeadRow>> = [
  { key: 'opponent', label: 'Opponent', sort: row => row.opponentDisplayName },
  {
    key: 'record',
    label: 'Record',
    className: 'record',
    hint: 'Sort by wins, then by fewest losses',
    sort: row => recordSortKey(row.wins, row.losses),
  },
  {
    key: 'pointsFor',
    label: 'PF',
    className: 'num',
    sortLabel: 'points for',
    sort: row => row.pointsFor,
  },
  {
    key: 'pointsAgainst',
    label: 'PA',
    className: 'num',
    sortLabel: 'points against',
    sort: row => row.pointsAgainst,
  },
]

function OpponentTable({ rows }: { rows: HeadToHeadRow[] }) {
  const sort = useTableSort(rows, OPPONENT_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={OPPONENT_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(row => (
            <tr key={row.opponentUserId}>
              <td>
                <Link
                  className="player__name--link"
                  to={playerProfilePath(row.opponentUserId)}
                >
                  {row.opponentDisplayName}
                </Link>
              </td>
              <td className="record">{formatRecord(row.wins, row.losses, row.ties)}</td>
              <td className="num">{row.pointsFor}</td>
              <td className="num">{row.pointsAgainst}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const SEASON_HISTORY_COLUMNS: Array<SortableColumn<SeasonRecordBreakdown>> = [
  { key: 'season', label: 'Season', className: 'num', sort: row => row.seasonNumber },
  {
    key: 'conference',
    label: 'Conf',
    sortLabel: 'conference',
    sort: row => row.conference,
  },
  {
    key: 'record',
    label: 'Record',
    className: 'record',
    hint: 'Sort by wins, then by fewest losses',
    sort: row => recordSortKey(row.wins, row.losses),
  },
  {
    key: 'diff',
    label: 'Diff',
    className: 'num',
    sortLabel: 'point differential',
    sort: row => row.pointsFor - row.pointsAgainst,
  },
]

function SeasonHistoryTable({ rows }: { rows: SeasonRecordBreakdown[] }) {
  const sort = useTableSort(rows, SEASON_HISTORY_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={SEASON_HISTORY_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(row => (
            <tr key={row.seasonNumber}>
              <td className="num">{row.seasonNumber}</td>
              <td>
                <ConferenceBadge conference={row.conference} />
              </td>
              <td className="record">{formatRecord(row.wins, row.losses, row.ties)}</td>
              <td className="num">{formatSigned(row.pointsFor - row.pointsAgainst)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function HeadToHeadLookup() {
  const players = useAsync(fetchLeaguePlayers, [])
  const [selected, setSelected] = useState<number | ''>('')

  const records = useAsync(
    () => (selected === '' ? Promise.resolve([]) : fetchHeadToHead(Number(selected))),
    [selected]
  )
  const history = useAsync(
    () => (selected === '' ? Promise.resolve([]) : fetchPlayerHistory(Number(selected))),
    [selected]
  )

  const sorted = useMemo(
    () => [...(players.data ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [players.data]
  )

  return (
    <div className="stack-sm">
      <label className="field" style={{ maxWidth: 320 }}>
        <span className="field__label">Player</span>
        <select
          className="select"
          value={selected}
          onChange={event => setSelected(event.target.value ? Number(event.target.value) : '')}
        >
          <option value="">Select a player…</option>
          {sorted.map(player => (
            <option key={player.id} value={player.id}>
              {player.displayName}
            </option>
          ))}
        </select>
      </label>

      {selected === '' ? (
        <EmptyState title="Pick a player to see their head-to-head record" />
      ) : records.loading ? (
        <Loading rows={5} />
      ) : (
        <div className="grid-2">
          <div>
            <h3 style={{ marginBottom: 12 }}>Versus every opponent</h3>
            {records.data && records.data.length > 0 ? (
              <OpponentTable rows={records.data} />
            ) : (
              <EmptyState title="No games on record for this player" />
            )}
          </div>

          <div>
            <h3 style={{ marginBottom: 12 }}>Season by season</h3>
            {history.data && history.data.length > 0 ? (
              <SeasonHistoryTable rows={history.data} />
            ) : (
              <EmptyState title="No season history yet" />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * The full player-vs-player grid.
 *
 * @remarks
 * Deliberately not sortable. Its rows and its columns are the same list of
 * players in the same order, which is what puts the em-dash diagonal on the
 * diagonal and makes a cell and its mirror readable as one rivalry. Reordering
 * the rows alone would break both, and there is no single column to sort by
 * anyway — every column is a different opponent.
 */
function Matrix() {
  const matrix = useAsync(() => fetchHeadToHeadMatrix(), [])

  if (matrix.loading) return <Loading rows={8} />
  if (matrix.error) return <Notice tone="error">{matrix.error}</Notice>
  if (!matrix.data || matrix.data.players.length === 0) {
    return <EmptyState title="Not enough games to build a matrix yet" />
  }

  const { players, cells } = matrix.data

  return (
    <div className="table-wrap">
      <table className="data matrix">
        <thead>
          <tr>
            <th className="matrix__corner">Player</th>
            {players.map(player => (
              <th key={player.id} className="num matrix__head" title={player.displayName}>
                {player.displayName.slice(0, 3).toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map(row => (
            <tr key={row.id}>
              <th className="matrix__row-head" title={row.displayName}>
                {row.displayName}
              </th>
              {players.map(column => {
                if (row.id === column.id) {
                  return (
                    <td key={column.id} className="num matrix__self">
                      —
                    </td>
                  )
                }
                const cell = cells[`${row.id}:${column.id}`]
                if (!cell) {
                  return (
                    <td key={column.id} className="num muted">
                      ·
                    </td>
                  )
                }
                const tone =
                  cell.wins > cell.losses ? 'is-up' : cell.wins < cell.losses ? 'is-down' : ''
                return (
                  <td key={column.id} className={`num ${tone}`}>
                    {formatRecord(cell.wins, cell.losses, cell.ties)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function NerdData() {
  // Defaults to every game, which is what a career total means everywhere else
  // on the site — the filter is here to answer a narrower question on request,
  // not to change what this table says by default.
  const [careerGameType, setCareerGameType] = useState<GameTypeFilter>('all')

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Nerd Data</span>
          <h1>Career totals and head-to-head</h1>
          <p>
            Everything cross-season: lifetime records, the full player-vs-player grid, and each
            player's season-by-season arc.
          </p>
        </div>
      </header>

      <Card
        title="Career records"
        subtitle="All seasons combined"
        actions={
          <Segmented
            options={GAME_TYPE_OPTIONS}
            value={careerGameType}
            onChange={setCareerGameType}
          />
        }
        flush
      >
        <CareerTable gameType={careerGameType} />
      </Card>

      <Card title="Head-to-head lookup" subtitle="Pick a player to break down their rivalries">
        <HeadToHeadLookup />
      </Card>

      <Card
        title="Player vs player matrix"
        subtitle="Row player's record against each column player"
        flush
      >
        <Matrix />
      </Card>
    </div>
  )
}
